import React, {lazy, useEffect, useRef, useState, Suspense} from 'react'
import {observer} from 'mobx-react'
import {when} from "mobx"
import {Router, Route, Switch, Redirect} from 'react-router-dom'
import IdleTimer from "react-idle-timer";

import './App.scss';

import {createTheme} from '@mui/material/styles';
import {ThemeProvider} from "@mui/styles";
import ConditionalComponent from "./Components/Shared/ConditionalComponent";
import {PROJECT_PATH_PREFIX} from "./MobxStores/ProjectStore";
import Settings from "./settings.json";
import {GeneralRequester} from "./MobxStores/RequestApi/GeneralRequester";
import Cookies from "js-cookie";
import WaitingWorkspace from "./Components/OnBoarding/WaitingWorkspace";
import MobileUserPage from "./Components/OnBoarding/MobileUserPage";
import {useStores} from "./Hooks";
import ModalsContainer from "./Components/Modals/ModalsContainer";
import {ConstantLoaderModal} from "./Components/Modals/LoaderModal";
import {create} from "jss";
import {jssPreset, StylesProvider} from "@mui/styles";
import rtl from "jss-rtl";
import {dataStore} from "./index";
import DomainPayPerClick from "./Components/Domain/DomainPayPerClick";
import FastTrack from "./Components/FastTrack/FastTrack";
import ConventionSignup from "./Components/OnBoarding/Convention/ConventionSignup";
import sharedUtils, {
    DEVICE_LOGIN_DOMAIN,
    HP_DEVICE_LOGIN_DOMAIN,
    FAST_TRACK_DOMAIN,
    CONVENTION_LOGIN_DOMAIN, FAST_TRACK_VERIFY_DOMAIN
} from "./serverClientShared/sharedUtils";
import DeviceLogin from "./Components/DeviceLogin/DeviceLogin";
import {ReactFlowProvider} from "react-flow-renderer";
import {ErrorBoundary} from "./ErrorBoundary";
import FastTrackVerify from "./Components/FastTrack/FastTrackVerify";
import Utility from "./Utility";
import WorkspaceDeployment from "./Components/Domain/WorkspaceDeployment";
import {ProcedureRequester} from "./MobxStores/RequestApi/ProcedureRequester";
import GlobalUtility from "./Utilities/GlobalUtility";

const ProcedurePlayer = lazy(() => import('./Components/RunProcedure/ProcedurePlayer'));
const ProjectLayout = lazy(() => import('./layouts/Project'));
const MyProjects = lazy(() => import('./layouts/MyProjects'));
const LoginPage = lazy(() => import('./Components/OnBoarding/Login/LoginPage'));
const SignupPage = lazy(() => import('./Components/OnBoarding/SignupPage'));
const ForgotPassword = lazy(() => import('./Components/OnBoarding/ForgotPassword'));
const ResetPassword = lazy(() => import('./Components/OnBoarding/ResetPassword'));
const SecureRoute = lazy(() => import('./SecureRoute'));
const DomainSSO = lazy(() => import('./Components/Domain/DomainSSO'));
const ExportPowerBI = lazy(() => import('./Components/Domain/PowerBI/ExportPowerBI'));

const WebGLEditor = lazy(() => import( './WebGL/WebGLEditor'));
const ViewProcedure = lazy(() => import("./Components/ViewProcedure/ViewProcedure"));
const RemoteSupportIframe = lazy(() => import("./Components/RemoteSupport/RemoteSupportIframe"));
const AccountSettings = lazy(() => import("./layouts/AccountSettings"));
const Page404 = lazy(() => import("./layouts/Page404"));

const WorkspaceSettings = lazy(() => import("./Components/Domain/WorkspaceSettings"));
const SubWorkspaceManagement = lazy(() => import("./Components/Domain/SubWorkspaceManagment/SubWorkspaceManagemnt"));
const AiStudio = lazy(() => import("./Components/Domain/AiStudio/MainAiStudio"));
const WorkspaceLanguage = lazy(() => import("./Components/Domain/WorkspaceLanguage"));
const QrCode = lazy(() => import("./Components/Domain/QrCode/StaticQrCodePage"))
const Integrations = lazy(() => import('./Components/Domain/Integrations/Integrations'))
const RemoteSupportManagement = lazy(() => import('./Components/Domain/RemoteSupportManagement/RemoteSupportManagement'))
const Members = lazy(() => import('./Components/Domain/Groups/Members'));
const Groups = lazy(() => import('./Components/Domain/Groups/Groups'));
const Roles = lazy(() => import('./Components/Domain/Groups/Roles'));
const WorkspaceUserDetails = lazy(() => import('./Components/Domain/Groups/WorkspaceUserDetails'));

const NotificationsSettings = lazy(() => import("./Components/Shared/NotificationsSettings"));
const ProcedureEditor = lazy(() => import("./Components/Editor/ProcedureEditor"));
const SurveyPage = lazy(()=> import("./Components/Survey/SurveyPage"));

export const routes = {
    CHANGE_EMAIL: "/deep/change-email",
    DEEP_LINK_VIEWER: "/deep/view/:db/:procedureId",
    DEEP_LINK_VIEWER_MODE: "/deep/view/:db/:procedureId/:modeIndex",
    DEEP_LINK_VIEWER_MODE_CONNECTED: "/deep/view/:db/:procedureId/:modeIndex/:connectedProcedure",
    DEEP_LINK_FLDT: "/deep/createFLDT/:db/:projectId",
    OPEN_SUPPORT_ROOM_WITH_PASSWORD: "/deep/meet/:roomId/:password",
    CREATE_SUPPORT_ROOM: "/deep/create-meet/",
    OPEN_VIRTUAL_ROOM: "/deep/virtualroom/:roomId",
    ACCEPT_INVITE: "/deep/accept-invite/:db/:validationId",
    OPEN_NOTIFICATIONS: "/deep/open-notifications",
    EDIT_INTERACTIONS: "deep/edit-interactions/:db/:procedureId",
    NOTIFICATION_SETTINGS: "/notification-settings",
    PARTS_CATALOG: "deep/parts-catalog/:db/:procedureId",
    PROJECT_DASHBOARD: PROJECT_PATH_PREFIX + "/dashboard",
    PROJECT_PROCEDURES: PROJECT_PATH_PREFIX + "/procedures",
    PROJECT_ANALYTICS: PROJECT_PATH_PREFIX + "/analytics",
    PROJECT_ROUTES: PROJECT_PATH_PREFIX,
    MAIN_DASHBOARD: '/maindashboard',
    EDITOR: '/edit/:db/:projectId/:folderId/:procedureId',
    OLD_EDITOR: '/old-editor/:db/:projectId/:procedureId',
    ACCOUNT_SETTINGS: '/account-settings',
    MEMBERS: '/members',
    GROUPS: '/groups',
    ROLES: '/roles',
    WORKSPACE_SETTINGS: '/workspace-settings',
    SUB_WORKSPACE_MANAGEMENT: '/sub-workspace-management',
    AI_STUDIO: '/ai-studio',
    GLOBAL_AI_STUDIO: '/global-ai-studio',
    WORKSPACE_LANGUAGE: '/workspace-language',
    WORKSPACE_USER_DETAILS: '/workspace-user/:userId',
    WORKSPACE_PAY_PER_CLICK: '/workspace-pay-per-click',
    SSO: '/sso',
    POWER_BI_EXPORT: '/power-bi-export',
    WORKSPACE_DEPLOYMENT: '/workspace-deployment',
    VIEWER: '/view/:db/:procedureId',
    SHORT_URL: '/s/:id',
    EMBEDDED_PLAYER: '/run/:db/:record/:procedureId',
    EMBEDDED_PLAYER_VIEWER_MODE: "/run/:db/:record/:procedureId/:modeIndex",
    EMBEDDED_PLAYER_MODE_CONNECTED: "/run/:db/:record/:procedureId/:modeIndex/:connectedProcedure",
    LOGIN: "/login",
    SIGNUP: "/signup",
    FORGOT_PASSWORD: "/forgotPassword",
    RESET_PASSWORD: "/resetPassword/:token",
    NOT_FOUND: "/404",
    ROOT: "/",
    TERMS: '/terms', //TODO
    // PRIVACY: '/privacy', //TODO
    LOGOUT: '/logout',
    WAITING_WORKSPACE: '/waiting-workspace',
    MOBILE_USER: '/mobile-user',
    DOWNLOAD_APP: '/download-app',
    QR_CODE: '/qr-code',
    Integrations: '/integrations',
    RemoteSupportManagement: '/remote-support-management',
    SURVEY: '/survey/:surveyType/:surveyIdentifier',
};

const muiSettings = {
    typography: {
        fontFamily: [
            'Open Sans', 'sans-serif'
        ].join(','),
    },
    palette: {
        primary: {
            light: "#2B3E79",
            main: "#222C3F",
            dark: "#1B2851",
        },
        secondary: {
            main: "#FFFFFF",
        },
        info: {
            main: "#006DFF",
        },
    },
}

const themeLtr = createTheme({direction: "ltr", ...muiSettings});
const themeRtl = createTheme({direction: "rtl", ...muiSettings});

const jss = create({plugins: [...jssPreset().plugins, rtl()]});

const App = observer(props => {
    const {generalStore, modalStore, procedureStore, rtcStore} = useStores();
    const [loading, setLoading] = useState(true);
    const idleTimer = useRef(null);

    const {loggedIn, workspace} = generalStore;

    window.addEventListener(
        "resize", getSizes, false);

    function getSizes() {
        generalStore.zoomLevel = (Math.ceil(((window.outerWidth - 10) / window.innerWidth) * 100));
    }


    function renderShortUrl() {
        return (props) => {
            const {id} = props.match.params;
            ProcedureRequester.getFullUrl(id).then(({url}) => {
                if (url) {
                    if (url.startsWith('/')) {
                        GlobalUtility.redirectInternalUrl(url, props)
                    } else {
                        GlobalUtility.redirectExternalUrl(url, props)
                    }
                }
            });
            return null;
        };
    }

    const applyZohoStyles = () => {
        const zohoWidget = document.querySelector('#zohohc-asap-web-app-main');
        const zohoButton = document.querySelector('#zohohc-asap-web-button');

        if (!zohoWidget) return false;
        if (!generalStore.userDetails?.DisableHelpButton && generalStore.showHelpDesk) {
            zohoWidget.style.setProperty('display', 'block', 'important'); // Override the display property with !important
            zohoWidget.style.setProperty('z-index', '1200', 'important'); // make it behind
            zohoWidget.style.setProperty('position', 'relative', 'important'); //
            zohoButton.style.setProperty('z-index', '998', 'important');
        } else {
            zohoWidget.style.setProperty('display', 'none', 'important'); // Hide the widget with !important
        }

        return true;
    };

    useEffect(() => {
        if (applyZohoStyles()) return;
        let timeoutId;
        const observer = new MutationObserver((mutations, obs) => {
            for (const mutation of mutations) {
                for (const node of mutation.addedNodes) {
                    if (node.nodeType === 1) {
                        if (node.id === 'zohohc-asap-web-app-main' || node.querySelector?.('#zohohc-asap-web-app-main')) {
                            if (applyZohoStyles()) {
                                obs.disconnect();
                                clearTimeout(timeoutId);
                                return;
                            }
                        }
                    }
                }
            }
        });
        observer.observe(document.body, {childList: true, subtree: true});
        timeoutId = setTimeout(() => {
            observer.disconnect();
        }, 10000);
        return () => {
            observer.disconnect();
            clearTimeout(timeoutId);
        };
    }, [generalStore.userDetails, generalStore.showHelpDesk, generalStore.theme]);

    useEffect(() => {
        document.addEventListener('fullscreenchange', onFullscreenChange);
        // document.body.classList.add(getDirectionClass());


        const urlParams = new URLSearchParams(window.location.search);
        const tokenParam = urlParams.get('t') || urlParams.get('token');
        const deviceIdParam = urlParams.get('d');
        const email = urlParams.get('email');
        if (email) {
            generalStore.signInData.email = email;
            rtcStore.emailToInvite = email;
        }

        const deviceId = urlParams.get('deviceId');

        if (deviceId) {
            rtcStore.deviceIdInvite = deviceId;
        }

        const firstName = urlParams.get('firstName');
        if (firstName) {
            generalStore.signInData.firstName = firstName;
        }
        const lastName = urlParams.get('lastName');
        if (lastName) {
            generalStore.signInData.lastName = lastName;
        }
        const deviceLoginCode = urlParams.get('devicecode');
        if (deviceLoginCode) {
            generalStore.deviceLoginCode = deviceLoginCode;
        }
        if (tokenParam) {
            Cookies.set(Settings.tokenCookieName, tokenParam, {expires: 90});
        }
        if (deviceIdParam) {
            Cookies.set('deviceId_v2', deviceIdParam, {expires: 365});
        }
        generalStore.isOpenInApp = urlParams.get('openapp') === '1';
        if (!generalStore.isOpenInApp && Cookies.get('close_tab') === '1') {
            window.close();
        }

        const redirectParam = urlParams.get('redirect');
        if (redirectParam) {
            if (!generalStore.loggedIn) {
                generalStore.redirectToAfterLogin = redirectParam;
            } else {
                props.history.push(redirectParam);
            }
        } else if (tokenParam) { // avoid keeping the temp token
            props.history.push(window.location.pathname);
        }

        Promise.all([generalStore.setLoggedIn(), generalStore.setWorkspaceConfig()]).then(() => {
            setLoading(false);
        });

        generalStore.setGlobalLanguage().then()
        setWebView(urlParams);

        setupDoceboParams(urlParams);
        if (urlParams.get('webglOnly')) {
            procedureStore.play3d = true;
        }

        if (Settings.frontlineArServer.includes('dev')) {
            Utility.replaceFaviconsAndTitle('dev', 'frontline.io DEV');
        }
        if (Settings.frontlineArServer.includes('localhost')) {
            Utility.replaceFaviconsAndTitle('dev', 'frontline.io LOCAL');
        }

        return () => {
            document.removeEventListener('fullscreenchange', onFullscreenChange);
        }
        // eslint-disable-next-line
    }, []);

    function setupDoceboParams(urlParams) {
        const endpoint = urlParams.get('endpoint');
        const auth = urlParams.get('auth');
        const activityId = urlParams.get('activity_id');
        const actor = urlParams.get('actor');
        const registration = urlParams.get('registration');
        if (endpoint && auth && activityId && actor && registration) {
            const actorJson = JSON.parse(actor);
            const actorName = Array.isArray(actorJson.name) ? actorJson.name[0] : actorJson.name;
            const mbox = Array.isArray(actorJson.mbox) ? actorJson.mbox[0] : actorJson.mbox;

            procedureStore.doceboParams = {
                endpoint,
                auth,
                activityId,
                actorName,
                mbox,
                registration
            }
        }
    }

    function onFullscreenChange() {
        generalStore.isInFullscreen = !!document.fullscreenElement;
    }

    async function finishChangeEmail() {
        generalStore.isLoaderOpen = true;
        try {
            // noinspection UnnecessaryLocalVariableJS
            const newEmail = await generalStore.finishChangeEmail();
            generalStore.userDetails.Email = newEmail;
            props.history.push(routes.LOGIN);
            await generalStore.refreshCurrentUser()
        } catch (e) {
            console.log(e.message);
            modalStore.showErrorMessage(generalStore.translate('couldNotReplaceEmail'));
            props.history.push(routes.LOGIN);
        }
        generalStore.isLoaderOpen = false;
    }

    async function openNotifications() {
        generalStore.isLoaderOpen = true;
        try {
            // await generalStore.refreshCurrentUser();
            dataStore.notificationsDataExpiration = Date.now() - 1;
            await generalStore.getAllNotifications();
        } catch (e) {
            console.log(e.message);
            modalStore.showErrorMessage(generalStore.translate('errorLoadingNotifications'));
        }
        props.history.push(routes.MAIN_DASHBOARD);
        generalStore.notificationsPanelOpen = true;
        generalStore.isLoaderOpen = false;
    }

    // TODO fix in the future
    function setWebView(urlParams) {
        const token = urlParams.get('token');
        const deviceId = urlParams.get('deviceId_v2');
        const hideTop = urlParams.get('hideTop');
        if (token && deviceId) {
            Cookies.set(Settings.tokenCookieName, token, {expires: 90});
            Cookies.set('deviceId_v2', deviceId, {expires: 365});
            generalStore.setLoggedIn(token).then();
            generalStore.isWebView = true;
            if (!!hideTop) {
                generalStore.hideTopBar = true;
            }
            generalStore.showHelpDesk = false;
        }
    }

    function handleOnIdle() {
        generalStore.setIdleState(true);
    }

    function handleOnActive() {
        generalStore.setIdleState(false);
    }

    function renderAcceptInvite(routeProps) {
        const {validationId, db} = routeProps.match.params;
        if (!validationId) {
            props.history.push(routes.NOT_FOUND)
            return null;
        }

        let isHeUser;
        GeneralRequester.getInviteEmail(db, validationId).then(({email, isUser}) => {
            isHeUser = isUser;
            generalStore.signInData.email = email;
        }).catch(() => {
        }).finally(() => {
            props.history.push(isHeUser ? routes.LOGIN : routes.SIGNUP);
        });
        return null;
    }

    function handleDeepLink() {
        const link = window.location.href;
        const deepLink = `procedureio://frontline-ar?d=${link}`;
        window.location.href = deepLink;
        modalStore.modalData = {
            deepLink
        }
        modalStore.isFrontlineArDialogOpen = true;
        return null;
    }

    function renderDownloadApp() {
        return () => {
            modalStore.isFrontlineArDialogOpen = true;
            return null;
        };
    }

    function renderEmbedded() {
        return (props) => {
            generalStore.showHelpDesk = false;
            const {procedureId, db} = props.match.params;
            return (
                <ProcedurePlayer
                    db={db}
                    procedureId={procedureId}
                    {...props}
                />
            )
        };
    }

    function renderRouts() {
        if (workspace === FAST_TRACK_DOMAIN) {
            return <FastTrack/>
        }
        if (workspace === FAST_TRACK_VERIFY_DOMAIN) {
            return <FastTrackVerify/>
        }
        if (workspace === DEVICE_LOGIN_DOMAIN || workspace === HP_DEVICE_LOGIN_DOMAIN) {
            return <DeviceLogin/>
        }
        if (workspace === CONVENTION_LOGIN_DOMAIN) {
            return <ConventionSignup/>
        }
        return <Suspense fallback={<ConstantLoaderModal/>}>
            <div className="App" onClick={generalStore.blurIntercom}>
                {/*<HelpDeskFab/>*/}
                <IdleTimer
                    ref={idleTimer}
                    timeout={(Settings.dataPollInterval + 30) * 1000}
                    onActive={handleOnActive}
                    onIdle={handleOnIdle}
                    debounce={250}
                />
                <Switch>
                    <SecureRoute
                        path={routes.CHANGE_EMAIL}
                        render={() => {
                            finishChangeEmail().then();
                            return null;
                        }}
                    />
                    <SecureRoute
                        path={routes.NOTIFICATION_SETTINGS}
                        component={NotificationsSettings}
                    />
                    <SecureRoute
                        path={routes.OPEN_NOTIFICATIONS}
                        render={() => {
                            openNotifications().then();
                            return null;
                        }}
                    />
                    <Route path={routes.PROJECT_ROUTES} component={ProjectLayout}/>
                    <SecureRoute exact path={routes.MAIN_DASHBOARD} component={MyProjects}/>
                    <SecureRoute exact path={routes.EDITOR} component={ProcedureEditor}/>
                    <SecureRoute exact path={routes.OLD_EDITOR} component={WebGLEditor}/>
                    <SecureRoute exact path={routes.ACCOUNT_SETTINGS} component={AccountSettings}/>
                    <SecureRoute exact path={routes.MEMBERS} component={Members} requireAdmin/>
                    <SecureRoute exact path={routes.GROUPS} component={Groups} requireAdmin/>
                    <SecureRoute exact path={routes.ROLES} component={Roles} requireAdmin/>
                    <SecureRoute exact path={routes.WORKSPACE_SETTINGS} component={WorkspaceSettings} requireAdmin/>
                    <SecureRoute exact path={routes.SUB_WORKSPACE_MANAGEMENT} component={SubWorkspaceManagement}
                                 requireAdmin/>
                    <SecureRoute key="ai-studio" exact path={routes.AI_STUDIO}
                                 render={() => <AiStudio isGlobal={false}/>} requireAdmin/>
                    <SecureRoute key="global-ai-studio" exact path={routes.GLOBAL_AI_STUDIO}
                                 render={() => <AiStudio isGlobal={true}/>} requireAdmin/>
                    <SecureRoute exact path={routes.WORKSPACE_LANGUAGE} component={WorkspaceLanguage} requireAdmin/>
                    <SecureRoute exact path={routes.WORKSPACE_PAY_PER_CLICK} component={DomainPayPerClick}
                                 requireAdmin/>
                    <SecureRoute exact path={routes.SSO} component={DomainSSO}
                                 requireAdmin/>
                    <SecureRoute exact path={routes.POWER_BI_EXPORT} component={ExportPowerBI}
                                 requireAdmin/>
                    <SecureRoute exact path={routes.QR_CODE} component={QrCode}
                                 requireAdmin/>
                    <SecureRoute exact path={routes.Integrations} component={Integrations}
                                 requireAdmin/>
                    <SecureRoute exact path={routes.WORKSPACE_DEPLOYMENT} component={WorkspaceDeployment}
                                 requireAdmin/>
                    <SecureRoute exact path={routes.RemoteSupportManagement} component={RemoteSupportManagement} requireAdmin/>
                    <SecureRoute exact path={routes.WORKSPACE_USER_DETAILS} component={WorkspaceUserDetails} requireAdmin/>
                    <SecureRoute path={routes.CREATE_SUPPORT_ROOM} component={RemoteSupportIframe}/>
                    <Route exact path={routes.DEEP_LINK_VIEWER_MODE_CONNECTED} component={ViewProcedure}/>
                    <Route exact path={routes.DEEP_LINK_VIEWER_MODE} component={ViewProcedure}/>
                    <Route exact path={routes.DEEP_LINK_VIEWER} component={ViewProcedure}/>
                    <Route path={routes.OPEN_SUPPORT_ROOM_WITH_PASSWORD} component={RemoteSupportIframe}/>
                    <Route path={routes.OPEN_VIRTUAL_ROOM} render={handleDeepLink}/>
                    <Route path={routes.EDIT_INTERACTIONS} render={handleDeepLink}/>
                    <Route
                        exact
                        path={routes.ACCEPT_INVITE}
                        render={(props) => {
                            renderAcceptInvite(props);
                            return null;
                        }}
                    />
                    <Route
                        exact
                        path={routes.LOGOUT}
                        render={() => {
                            generalStore.deleteToken();
                            dataStore.deleteAll();
                            return null;
                        }}
                    />
                    <Route exact path={routes.VIEWER} component={ViewProcedure}/>
                    <Route exact path={routes.SHORT_URL} render={renderShortUrl()}/>
                    <Route exact path={routes.EMBEDDED_PLAYER} render={renderEmbedded()}/>
                    <Route exact path={routes.EMBEDDED_PLAYER_VIEWER_MODE} render={renderEmbedded()}/>
                    <Route exact path={routes.EMBEDDED_PLAYER_MODE_CONNECTED} render={renderEmbedded()}/>
                    <Route exact path={routes.LOGIN} component={LoginPage}/>
                    <Route exact path={routes.SIGNUP} component={SignupPage}/>
                    <Route exact path={routes.FORGOT_PASSWORD} component={ForgotPassword}/>
                    <Route exact path={routes.RESET_PASSWORD} component={ResetPassword}/>
                    <Route exact path={routes.NOT_FOUND} component={Page404}/>
                    <Route exact path={routes.WAITING_WORKSPACE} component={WaitingWorkspace}/>
                    <Route exact path={routes.MOBILE_USER} component={MobileUserPage}/>
                    <Route exact path={routes.DOWNLOAD_APP} render={renderDownloadApp()}/>
                    <Route exact path={routes.SURVEY} component={SurveyPage}/>

                    <Route exact path={routes.ROOT} render={() => {
                        generalStore.preventLeaveActive = false;
                        if (loggedIn) {
                            when(() => !generalStore.preventLeaveActive).then(() => {
                                props.history.push(routes.MAIN_DASHBOARD)
                            });
                        } else if (!workspace || workspace !== sharedUtils.LOGIN_DOMAIN) {
                            generalStore.isLoaderOpen = true;
                            setTimeout(() => {
                                generalStore.goToLogin(false);
                            }, 1000);
                        }
                    }}/>
                    <Redirect to={routes.NOT_FOUND}/>
                </Switch>
            </div>
        </Suspense>;
    }

    return (
        <StylesProvider jss={jss}>
            <ReactFlowProvider>
                <ThemeProvider theme={generalStore.langDir === "rtl" ? themeRtl : themeLtr}>
                    <ConditionalComponent if={!loading}>
                        <Router history={props.history}>
                            <ErrorBoundary workspace={generalStore.workspace}>
                                {renderRouts()}
                            </ErrorBoundary>
                        </Router>
                    </ConditionalComponent>

                    <Suspense fallback={<ConstantLoaderModal/>}>
                        <ModalsContainer/>
                    </Suspense>
                </ThemeProvider>
            </ReactFlowProvider>
        </StylesProvider>
    )
});


export default App
